/**
 * @file useHomeBanner.js
 * @description 首页 Banner 轮播：循环预览 + 居中可点区域
 * @module features/home/composables/useHomeBanner
 */

import {
  computed,
  nextTick,
  onMounted,
  onUnmounted,
  ref,
  watch,
} from 'vue'
import { useRouter } from 'vue-router'
import { fetchHomeBannerListApi, fetchHomeBannerFallbackFramesApi } from '@/features/home/api/homeApi'
import {
  BANNER_ACTIVE_WIDTH_RATIO,
  BANNER_MAIN_POSITION,
  BANNER_POSITIONS,
  buildBannerSlotMap,
  getBannerImageUrl,
  getBannerTitle,
  navigateBannerComic,
  preloadBannerCoverUrls,
  resolveBannerClientType,
} from '@/features/home/utils/bannerLink'
import { bannerDebug } from '@/features/home/utils/bannerDebug'

/** @typedef {import('../api/homeApi').HomeBannerFrame} HomeBannerFrame */

export { BANNER_POSITIONS } from '@/features/home/utils/bannerLink'

/**
 * @param {HTMLElement|null|undefined} el
 */
function readViewportWidth(el) {
  const elWidth = el?.clientWidth ?? 0
  if (elWidth > 0) return elWidth
  if (typeof window !== 'undefined' && window.innerWidth > 0) {
    return window.innerWidth
  }
  return 0
}

/**
 * @param {HTMLElement|null|undefined} el
 */
function applyViewportCssVars(el, viewportWidth) {
  if (!el || viewportWidth <= 0) return
  el.style.setProperty(
    '--home-banner-frame-width',
    `${viewportWidth * BANNER_ACTIVE_WIDTH_RATIO}px`
  )
}

export function useHomeBanner() {
  const router = useRouter()

  const frames = ref(/** @type {HomeBannerFrame[]} */ ([]))
  const carouselIntervalMs = ref(5000)
  const bannerClientType = ref(/** @type {import('@/features/home/utils/bannerLink').HomeBannerClientType} */ ('WEB'))
  const isLoading = ref(true)
  const fetchFailed = ref(false)
  const activeIndex = ref(0)
  /** @type {import('vue').Ref<'forward' | 'backward' | 'auto'>} */
  const slideDirection = ref('forward')
  const viewportRef = ref(/** @type {HTMLElement|null} */ (null))
  const viewportWidth = ref(0)

  let autoPlayTimer = null
  let resizeObserver = null

  const hasItems = computed(() => frames.value.length > 0)
  const slideCount = computed(() => frames.value.length)
  const isMobileBanner = computed(() => bannerClientType.value === 'H5')

  /** H5 / WEB 滑动轨道：预渲染全部屏 */
  const allSlideSlotMaps = computed(() =>
    frames.value.map((_, index) => getSlotsForFrame(index))
  )

  /**
   * @param {number} index
   */
  function getSlotsForFrame(index) {
    const frame = frames.value[index]
    if (!frame) return buildBannerSlotMap([])
    return buildBannerSlotMap(frame.comics)
  }

  function clearAutoPlay() {
    if (autoPlayTimer) {
      clearInterval(autoPlayTimer)
      autoPlayTimer = null
    }
  }

  function startAutoPlay() {
    clearAutoPlay()
    if (frames.value.length < 2) return
    const interval = Math.max(carouselIntervalMs.value, 1000)
    autoPlayTimer = setInterval(() => {
      goNext()
    }, interval)
  }

  /** 用户手动切屏后重置自动轮播计时 */
  function restartAutoPlayAfterUserAction() {
    startAutoPlay()
  }

  function goToSlide(index) {
    if (!frames.value.length) return
    activeIndex.value =
      ((index % frames.value.length) + frames.value.length) %
      frames.value.length
  }

  function goToSlideByUser(index) {
    slideDirection.value = 'auto'
    goToSlide(index)
    restartAutoPlayAfterUserAction()
  }

  function goPrev() {
    slideDirection.value = 'backward'
    goToSlide(activeIndex.value - 1)
  }

  function goPrevByUser() {
    goPrev()
    restartAutoPlayAfterUserAction()
  }

  function goNext() {
    slideDirection.value = 'forward'
    goToSlide(activeIndex.value + 1)
  }

  function goNextByUser() {
    goNext()
    restartAutoPlayAfterUserAction()
  }

  function onComicClick(comic) {
    navigateBannerComic(router, comic)
  }

  function updateViewportWidth() {
    const nextWidth = readViewportWidth(viewportRef.value)
    viewportWidth.value = nextWidth
    applyViewportCssVars(viewportRef.value, nextWidth)
    bannerDebug('viewport-width', { nextWidth })
  }

  function bindResizeObserver() {
    unbindResizeObserver()
    if (!viewportRef.value) {
      updateViewportWidth()
      return
    }
    if (typeof ResizeObserver === 'undefined') {
      updateViewportWidth()
      return
    }
    resizeObserver = new ResizeObserver(() => {
      updateViewportWidth()
    })
    resizeObserver.observe(viewportRef.value)
    updateViewportWidth()
  }

  function unbindResizeObserver() {
    resizeObserver?.disconnect()
    resizeObserver = null
  }

  async function fetchBanner() {
    isLoading.value = true
    fetchFailed.value = false
    const clientType = resolveBannerClientType()
    try {
      const response = await fetchHomeBannerListApi(clientType)
      bannerClientType.value = response.clientType
      let nextFrames = response.frames
      if (nextFrames.length === 0) {
        nextFrames = await fetchHomeBannerFallbackFramesApi(clientType)
        bannerDebug('fallback-frames', { count: nextFrames.length, clientType })
      }
      frames.value = nextFrames
      carouselIntervalMs.value = response.carouselIntervalMs
      activeIndex.value = 0
      preloadBannerCoverUrls(nextFrames, response.clientType)
    } catch (error) {
      frames.value = []
      fetchFailed.value = true
      bannerDebug('fetch-error', error)
    } finally {
      isLoading.value = false
      await nextTick()
      bindResizeObserver()
    }
  }

  function handleViewportResize() {
    updateViewportWidth()
    const nextClientType = resolveBannerClientType()
    if (!isLoading.value && nextClientType !== bannerClientType.value) {
      fetchBanner()
    }
  }

  watch([frames, carouselIntervalMs], () => {
    startAutoPlay()
  })

  watch(hasItems, async (val) => {
    if (!val) return
    await nextTick()
    bindResizeObserver()
  })

  watch(viewportRef, async (el) => {
    if (!el) return
    await nextTick()
    bindResizeObserver()
  })

  onMounted(() => {
    fetchBanner()
    window.addEventListener('resize', handleViewportResize)
  })

  onUnmounted(() => {
    clearAutoPlay()
    unbindResizeObserver()
    window.removeEventListener('resize', handleViewportResize)
  })

  return {
    BANNER_MAIN_POSITION,
    viewportRef,
    isLoading,
    fetchFailed,
    hasItems,
    isMobileBanner,
    slideCount,
    activeIndex,
    slideDirection,
    allSlideSlotMaps,
    getBannerImageUrl,
    getBannerTitle,
    goPrev,
    goNext,
    goToSlide,
    goPrevByUser,
    goNextByUser,
    goToSlideByUser,
    onComicClick,
    refetchBanner: fetchBanner,
  }
}
